/**
 * Sprint 24.15 — HireWizardTemplateCustomise (step 3, template path).
 * Sprint 24.15.2 — install-flow + equipment-availability reconciliation.
 *
 * Shallow-customise form (Decision 6): display name, reports-to, instructions
 * addendum, optional-equipment toggles, model tier. Persona is read-only (it's
 * the template's identity). Submit → hire_from_template.
 *
 * On `equipment_missing` a modal (rewritten in 24.15.2) lists each missing kind
 * with its availability status and offers: Open Connections (→ filtered
 * Connections page, form state preserved via sessionStorage), Create agent
 * without them (hire unequipped, missing kinds deferred), or Cancel hire (drop
 * the draft, back to the gallery). Coming-soon kinds never block — the server
 * defers them — so an all-coming-soon template never raises the modal at all.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../../../lib/hooks/use-auth";
import { useListTree, useWorkspaceInfo } from "../../../lib/hooks/use-kuvio";
import { usePreviewAgentTemplate, type TierChoice } from "../../hooks/use-agent-templates";
import {
  useHireFromTemplate,
  EquipmentMissingError,
  type HireFromTemplateResult,
  type MissingEquipment,
} from "../../hooks/use-hire-from-template";
import { HireDraftReportsTo } from "./HireDraftReportsTo";
import { TemplateIcon, EquipmentStatusDot, EquipmentMeta, equipmentStatusMeta } from "./template-visuals";
import {
  clearHireDraft,
  hireTemplateReturnPath,
  loadHireDraft,
  saveHireDraft,
} from "./hire-template-handoff";
import { showToast } from "../primitives";

export interface HireWizardTemplateCustomiseProps {
  templateId: string;
  onBack: () => void;
  onHired: (result: HireFromTemplateResult) => void;
  /** Sprint 24.15.2 — "Cancel hire" abandons the draft and returns to the gallery. */
  onCancelToGallery: () => void;
}

// Sprint 24.15.1 — "Workspace default" is pinned first + pre-selected (maps to no
// model_tier_override → the agent inherits the workspace tier). Other three lock it.
const MODEL_TIERS: { tier: TierChoice; label: string; hint: string }[] = [
  { tier: "Workspace", label: "Workspace default", hint: "Follow workspace tier" },
  { tier: "Fast", label: "Fast", hint: "Quick + economical" },
  { tier: "Standard", label: "Standard", hint: "Balanced" },
  { tier: "Heavy", label: "Heavy", hint: "Most capable" },
];

export function HireWizardTemplateCustomise({
  templateId,
  onBack,
  onHired,
  onCancelToGallery,
}: HireWizardTemplateCustomiseProps) {
  const navigate = useNavigate();
  const { currentWorkspaceId } = useAuth();
  const workspace = useWorkspaceInfo();
  const tree = useListTree(currentWorkspaceId ?? undefined);
  const { data: template, isLoading, isError } = usePreviewAgentTemplate(templateId);
  const hire = useHireFromTemplate();

  const rootNodeId = (workspace.data?.root_node_id as string | undefined) ?? null;
  const workspaceName = workspace.data?.workspace?.name ?? "Workspace";
  const allNodes = tree.data ?? [];

  const [nameOverride, setNameOverride] = useState("");
  const [reportsTo, setReportsTo] = useState<string | null>(null);
  const [addendum, setAddendum] = useState("");
  const [tier, setTier] = useState<TierChoice>("Workspace");
  // Optional-equipment enablement, keyed by kind. Required stays checked + disabled.
  const [optionalOff, setOptionalOff] = useState<Record<string, boolean>>({});
  const [missing, setMissing] = useState<MissingEquipment[] | null>(null);

  // Sprint 24.15.2 — pick up an in-progress draft saved before a Connections
  // round-trip (Decision 9). Runs once per template id; stale drafts (>1h) are
  // ignored by loadHireDraft.
  const restoredRef = useRef<string | null>(null);
  useEffect(() => {
    if (restoredRef.current === templateId) return;
    restoredRef.current = templateId;
    const draft = loadHireDraft(templateId);
    if (!draft) return;
    setNameOverride(draft.nameOverride);
    setReportsTo(draft.reportsTo);
    setAddendum(draft.addendum);
    setTier(draft.tier);
    setOptionalOff(draft.optionalOff);
    // One-shot restore: drop the draft now that it's live in the form. A later
    // Connections round-trip re-saves it (openConnections persists first), so
    // this can't strand the round-trip — it just prevents a re-mount from
    // reverting edits made after the return.
    clearHireDraft(templateId);
    showToast("Picked up where you left off", "success");
  }, [templateId]);

  const equipmentOverrides = useMemo(
    () =>
      Object.entries(optionalOff)
        .filter(([, off]) => off)
        .map(([kind]) => ({ kind, enabled: false })),
    [optionalOff],
  );

  function persistDraft() {
    saveHireDraft(templateId, { nameOverride, reportsTo, addendum, tier, optionalOff });
  }

  /** Navigate to the Connections page, filtered to the given kinds, with a
   *  return path back to this customise step. Form state is persisted first. */
  function openConnections(kinds: string[]) {
    if (!template) return;
    persistDraft();
    const missingParam = kinds.join(",");
    navigate(
      `/connections?missing=${encodeURIComponent(missingParam)}` +
        `&return_to=${encodeURIComponent(hireTemplateReturnPath(template.id))}`,
    );
  }

  async function submit(allowMissing: boolean) {
    if (!template) return;
    try {
      const result = await hire.mutateAsync({
        template_id: template.id,
        name_override: nameOverride.trim() || undefined,
        instructions_addendum: addendum.trim() || undefined,
        equipment_overrides: equipmentOverrides.length > 0 ? equipmentOverrides : undefined,
        // Workspace default → omit (inherit workspace tier); otherwise lock the tier.
        model_tier_override: tier === "Workspace" ? undefined : tier,
        reports_to_node_id: reportsTo ?? undefined,
        allow_missing_equipment: allowMissing,
      });
      setMissing(null);
      clearHireDraft(template.id); // successful hire — drop the saved draft.
      onHired(result);
    } catch (err) {
      if (err instanceof EquipmentMissingError) {
        setMissing(err.missingEquipment);
        return;
      }
      showToast(err instanceof Error ? err.message : "Couldn't hire from template.", "error");
    }
  }

  function cancelHire() {
    clearHireDraft(templateId);
    setMissing(null);
    onCancelToGallery();
  }

  if (isLoading) {
    return (
      <div style={body}>
        <div style={{ ...skel, width: "50%", height: 14 }} />
        <div style={{ ...skel, width: "100%", height: 200 }} />
      </div>
    );
  }
  if (isError || !template) {
    return (
      <div style={body}>
        <div style={{ padding: 32, textAlign: "center", fontSize: 13, color: "var(--color-text-secondary)" }}>
          Couldn't load this template.{" "}
          <button type="button" onClick={onBack} style={linkBtn}>
            Back
          </button>
        </div>
      </div>
    );
  }

  const busy = hire.isPending;

  return (
    <div style={body}>
      <nav style={breadcrumb}>
        <button type="button" onClick={onBack} style={crumbLink}>
          {template.name}
        </button>
        <span style={crumbSep}>/</span>
        <span style={crumbCurrent}>Customise</span>
      </nav>

      <header style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <TemplateIcon icon={template.icon} ramp={template.ramp} size={44} />
        <div>
          <h3 style={h3}>Customise {template.name}</h3>
          <div style={subtitle}>Override any field — leave blank to inherit the template default.</div>
        </div>
      </header>

      {/* Display name */}
      <div style={field}>
        <label style={label} htmlFor="tpl-name">
          Display name
        </label>
        <input
          id="tpl-name"
          style={input}
          value={nameOverride}
          placeholder={template.name}
          onChange={(e) => setNameOverride(e.target.value)}
        />
      </div>

      {/* Reports to (reused 23.5 component) */}
      <div style={field}>
        <HireDraftReportsTo
          value={reportsTo}
          rootNodeId={rootNodeId}
          workspaceName={workspaceName}
          allNodes={allNodes}
          onChange={setReportsTo}
        />
      </div>

      {/* Instructions addendum */}
      <div style={field}>
        <label style={label} htmlFor="tpl-addendum">
          Instructions addendum <span style={{ color: "var(--color-text-tertiary)" }}>(optional)</span>
        </label>
        <textarea
          id="tpl-addendum"
          style={{ ...input, minHeight: 72, resize: "vertical", lineHeight: 1.5 }}
          value={addendum}
          placeholder="Workspace-specific guidance, appended below the template instructions."
          onChange={(e) => setAddendum(e.target.value)}
        />
      </div>

      {/* Equipment */}
      {template.default_equipment.length > 0 && (
        <div style={field}>
          <span style={label}>
            Equipment <span style={labelSubtitle}>— what this agent can use</span>
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {template.default_equipment.map((e) => {
              const meta = equipmentStatusMeta(e.availability_status, e.availability_note, e.kind);
              const comingSoon = e.availability_status === "coming-soon";
              const checked = e.required || !optionalOff[e.kind];
              // Coming-soon kinds aren't installable — no checkbox; a roadmap pill
              // replaces the Required/Optional badge (visual contract).
              return (
                <label key={e.kind} style={{ ...equipRow, cursor: comingSoon ? "default" : "pointer" }}>
                  <EquipmentStatusDot color={meta.dotColor} size={6} />
                  {comingSoon ? (
                    <span style={checkboxSpacer} aria-hidden="true" />
                  ) : (
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={e.required}
                      onChange={(ev) => setOptionalOff((m) => ({ ...m, [e.kind]: !ev.target.checked }))}
                    />
                  )}
                  <i className="ti ti-plug-connected" style={{ fontSize: 14, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
                  <EquipmentMeta name={e.kind} equipmentType={e.equipment_type} purpose={e.purpose} />
                  {comingSoon ? (
                    <span style={comingSoonBadge}>{e.availability_note ? `Coming ${e.availability_note}` : "Coming soon"}</span>
                  ) : (
                    <span style={e.required ? requiredBadge : optionalBadge}>{e.required ? "Required" : "Optional"}</span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Model tier */}
      <div style={field}>
        <span style={label}>
          Model tier <span style={labelSubtitle}>— how much brainpower this agent uses</span>
        </span>
        <div style={tierGrid}>
          {MODEL_TIERS.map((t) => {
            const selected = tier === t.tier;
            return (
              <button
                key={t.tier}
                type="button"
                onClick={() => setTier(t.tier)}
                style={{ ...tierCard, ...(selected ? tierCardSelected : tierCardUnselected) }}
              >
                <span style={{ fontSize: 13, fontWeight: 500 }}>{t.label}</span>
                <span style={{ fontSize: 11, color: selected ? "var(--color-text-info)" : "var(--color-text-tertiary)" }}>
                  {t.hint}
                </span>
              </button>
            );
          })}
        </div>
        <div style={tierHint}>
          <i className="ti ti-info-circle" style={{ fontSize: 12, flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
          <span>
            Workspace default lets this agent follow your workspace's tier setting — if your workspace tier changes
            later, this agent automatically follows. The other options lock the agent to a specific tier.
          </span>
        </div>
      </div>

      {/* Footer */}
      <div style={footer}>
        <button type="button" onClick={onBack} style={secondaryBtn} disabled={busy}>
          Back to preview
        </button>
        <button type="button" onClick={() => void submit(false)} style={primaryBtn} disabled={busy}>
          {busy ? "Hiring…" : "Confirm hire ✓"}
        </button>
      </div>

      {missing && (
        <EquipmentMissingModal
          template={template.name}
          missing={missing}
          busy={busy}
          onOpenConnections={openConnections}
          onCreateWithout={() => void submit(true)}
          onCancelHire={cancelHire}
          onClose={() => setMissing(null)}
        />
      )}
    </div>
  );
}

function EquipmentMissingModal({
  template,
  missing,
  busy,
  onOpenConnections,
  onCreateWithout,
  onCancelHire,
  onClose,
}: {
  template: string;
  missing: MissingEquipment[];
  busy: boolean;
  onOpenConnections: (kinds: string[]) => void;
  onCreateWithout: () => void;
  onCancelHire: () => void;
  onClose: () => void;
}) {
  const allKinds = missing.map((m) => m.kind);
  return (
    <div style={modalBackdrop} role="dialog" aria-modal="true" aria-label="Add missing connections" onClick={onClose}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h4 style={modalTitle}>Add missing connections first?</h4>
        <p style={modalBody}>
          The {template} template needs {missing.length} connection{missing.length === 1 ? "" : "s"} to work fully:
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {missing.map((m) => {
            const meta = equipmentStatusMeta(m.availability_status, m.availability_note, m.kind);
            return (
              <div key={m.kind} style={missingRow}>
                <EquipmentStatusDot color={meta.dotColor} size={8} />
                <span style={{ flex: 1, fontSize: 13, color: "var(--color-text-primary)" }}>{m.kind}</span>
                {meta.installable && meta.ctaLabel ? (
                  <button
                    type="button"
                    style={rowCtaBtn}
                    disabled={busy}
                    onClick={() => onOpenConnections([m.kind])}
                  >
                    {meta.ctaLabel}
                  </button>
                ) : (
                  <span style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>{meta.statusLabel}</span>
                )}
              </div>
            );
          })}
        </div>

        <div style={modalButtonRow}>
          <button type="button" onClick={onCancelHire} style={tertiaryBtn} disabled={busy}>
            Cancel hire
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={onCreateWithout} style={secondaryBtn} disabled={busy}>
              Create agent without them
            </button>
            <button type="button" onClick={() => onOpenConnections(allKinds)} style={primaryBtn} disabled={busy}>
              Open Connections
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const body: CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  padding: "16px 20px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const breadcrumb: CSSProperties = { display: "flex", alignItems: "center", gap: 6, fontSize: 13 };
const crumbLink: CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  fontSize: 13,
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const crumbCurrent: CSSProperties = { color: "var(--color-text-primary)" };
const crumbSep: CSSProperties = { color: "var(--color-text-tertiary)" };

const h3: CSSProperties = { margin: 0, fontSize: 16, fontWeight: 500, color: "var(--color-text-primary)" };
const subtitle: CSSProperties = { fontSize: 13, color: "var(--color-text-secondary)", marginTop: 2 };

const field: CSSProperties = { display: "flex", flexDirection: "column", gap: 6 };
const label: CSSProperties = { fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" };
const labelSubtitle: CSSProperties = { fontSize: 13, fontWeight: 400, color: "var(--color-text-tertiary)" };
const input: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  fontSize: 13,
  padding: "8px 10px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
};

const equipRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 14px",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-secondary)",
  cursor: "pointer",
};
const checkboxSpacer: CSSProperties = { width: 13, height: 13, flexShrink: 0 };
const requiredBadge: CSSProperties = {
  fontSize: 11,
  padding: "1px 7px",
  borderRadius: 999,
  background: "var(--color-background-danger)",
  color: "var(--color-text-danger)",
};
const optionalBadge: CSSProperties = {
  fontSize: 11,
  padding: "1px 7px",
  borderRadius: 999,
  background: "var(--color-background-secondary)",
  color: "var(--color-text-secondary)",
  border: "0.5px solid var(--color-border-tertiary)",
};
const comingSoonBadge: CSSProperties = {
  fontSize: 11,
  padding: "1px 7px",
  borderRadius: 999,
  background: "var(--color-background-warning)",
  color: "var(--color-text-warning)",
  whiteSpace: "nowrap",
};

const tierGrid: CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 };
const tierHint: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 6,
  marginTop: 6,
  fontSize: 11,
  lineHeight: 1.45,
  color: "var(--color-text-tertiary)",
};
const tierCard: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  alignItems: "flex-start",
  padding: "10px 12px",
  borderRadius: "var(--border-radius-md)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  textAlign: "left",
};
const tierCardSelected: CSSProperties = {
  border: "2px solid var(--color-border-info)",
  background: "var(--color-background-info)",
  color: "var(--color-text-info)",
};
const tierCardUnselected: CSSProperties = {
  border: "0.5px solid var(--color-border-tertiary)",
  background: "transparent",
  color: "var(--color-text-primary)",
};

const footer: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  paddingTop: 12,
  borderTop: "0.5px solid var(--color-border-tertiary)",
  marginTop: "auto",
};
const secondaryBtn: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "8px 16px",
  borderRadius: "var(--border-radius-md)",
  border: "0.5px solid var(--color-border-secondary)",
  background: "transparent",
  color: "var(--color-text-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const primaryBtn: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "8px 16px",
  borderRadius: "var(--border-radius-md)",
  border: "none",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const tertiaryBtn: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "8px 4px",
  border: "none",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const linkBtn: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-info)",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const skel: CSSProperties = { background: "var(--color-background-tertiary)", borderRadius: 4 };

const modalBackdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 300,
};
const modalCard: CSSProperties = {
  width: 440,
  maxWidth: "90vw",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 20,
  background: "var(--color-background-primary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-lg)",
  boxShadow: "0 24px 64px rgba(0, 0, 0, 0.32)",
};
const modalTitle: CSSProperties = { margin: 0, fontSize: 16, fontWeight: 500, color: "var(--color-text-primary)" };
const modalBody: CSSProperties = {
  margin: 0,
  fontSize: 13,
  lineHeight: 1.55,
  color: "var(--color-text-secondary)",
};
const missingRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 12px",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-secondary)",
};
const rowCtaBtn: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  padding: "2px 8px",
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-border-info)",
  background: "transparent",
  color: "var(--color-text-info)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
};
const modalButtonRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  marginTop: 4,
};
